import PropTypes from 'prop-types'
import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getHeaderState } from '../../../../../store/selectors'
import { headerActions } from '../../../../../store/slices'

import {
  Accordion,
  ButtonGroup,
  Checkbox,
  Dropdown,
  FormControl,
  FormGroup,
  FormLabel,
} from '../../../../../../../components'
import CommonSelector from './CommonSelector'
import IJoistSelector from './IJoistSelector'

import {
  getDefaultOptValue,
  getDefaultPlyValue,
  getGAValue,
  isMemberTypeLedger,
  shouldDisplayLumberFinish,
  shouldDisplayNumberOfPlies,
} from '../../../../../common/fns'
import { useCalcActions, useCommonOptions } from '../../../../../common/hooks'
import { header } from '../../../../../common/json/carrying-member.json'
import { helpText } from '../../../../../common/json/helpText.json'

import { IJOIST_INPUT_TYPE } from '../../../../../../../utils/constants'
import { RenderIf, isNull, isOneDropdownOption } from '../../../../../../../utils/fns'
import {
  HEADER_TYPE_LABEL,
  IJOIST_INPUT_OPTIONS,
  MEMBER_TYPE,
} from '../../../../../common/constants'

const Header = (props) => {
  const { country } = props

  const dispatch = useDispatch()
  const state = useSelector(getHeaderState)

  const { errors, validateField, setCalcValue, updateCalcValue } = useCalcActions(headerActions)
  const { pliesOptions, ...optionsData } = useCommonOptions(state, country, header)

  const handleChangeMemberType = (e) => updateCalcValue({ ...header.defaultValue[e.target.value] })

  const plyLabel = useMemo(
    () =>
      [
        MEMBER_TYPE.WALL_DRYWALL_FLUSH,
        MEMBER_TYPE.WALL_DRYWALL_FLUSH_STUD,
        MEMBER_TYPE.WALL_DRYWALL_GAP,
        MEMBER_TYPE.WALL_SHEATED_FLUSH,
        MEMBER_TYPE.WALL_SHEATED_GAP,
      ].includes(state.type)
        ? 'Top Plate'
        : 'Number of Plies',
    [state.type],
  )

  const handleChangeLumberSpecies = (e) => {
    setCalcValue(e)

    if (!isMemberTypeLedger(state.type)) return

    dispatch(
      headerActions.modifyCalculation({
        name: 'isRough',
        value: shouldDisplayLumberFinish(state.type, e.target.value) ? false : null
      }),
    )
  }

  useEffect(() => {
    const isWidthExists = optionsData.widthOptions.some((item) => item.value === state.width)

    if (!isWidthExists && optionsData.widthOptions.length > 0) {
      const value = getDefaultOptValue(optionsData.widthOptions)
      dispatch(headerActions.modifyCalculation({ name: 'width', value }))
    }
  }, [state.material, optionsData.widthOptions])

  useEffect(() => {
    const isDepthExists = optionsData.depthOptions.some((item) => item.value === state.depth)

    if (!isDepthExists && optionsData.depthOptions.length > 0) {
      const value = getDefaultOptValue(optionsData.depthOptions)
      dispatch(headerActions.modifyCalculation({ name: 'depth', value }))
    }
  }, [state.width, optionsData.depthOptions])

  useEffect(() => {
    const isPlyExists = pliesOptions.some((item) => item.value === state.ply)

    if (!isPlyExists || !shouldDisplayNumberOfPlies(state.type, pliesOptions, state.isRough)) {
      dispatch(
        headerActions.modifyCalculation({ name: 'ply', value: getDefaultPlyValue(pliesOptions) }),
      )
    }
  }, [state.depth, state.isRough, state.type, state.ply, pliesOptions])

  useEffect(() => {
    const isThicknessExists = optionsData.thicknessOptions.some(
      (item) => item.value === state.thickness,
    )

    if (!isThicknessExists && optionsData.thicknessOptions.length > 0) {
      const value = getDefaultOptValue(optionsData.thicknessOptions)
      dispatch(headerActions.modifyCalculation({ name: 'thickness', value }))
    }
  }, [state.material, optionsData.thicknessOptions])

  return (
    <Accordion title="Header (Carrying Member)">
      {state.type === 'ijoist' && (
        <ButtonGroup
          className="row"
          colClassName="col-6"
          name="ijoistInputType"
          options={IJOIST_INPUT_OPTIONS}
          value={state.ijoistInputType}
          onChange={(name, value) => updateCalcValue({ [name]: value })}
          containerProps={{ 'data-testid': 'header_ijoistInputType' }}
        />
      )}

      <div className="grid grid__auto--250 grid--gap-1-2">
        {/* remove popperText={helpText[country].memberType} per Russ's request 1/20/22 */}
        <FormGroup>
          <FormLabel htmlFor="header.type">Member Type</FormLabel>
          <Dropdown
            name="header.type"
            value={state.type}
            options={header.memberTypeData}
            onChange={handleChangeMemberType}
            data-ga4={`header_memberType:${HEADER_TYPE_LABEL[state.type]}`}
            data-testid="header.type"
          />
        </FormGroup>

        {state.type === 'ijoist' &&
        // (mode === 0 )
        state.ijoistInputType === IJOIST_INPUT_TYPE.MANUFACTURER ? (
          <IJoistSelector
            label="header"
            material={state.material}
            manufacturer={state.manufacturer}
            series={state.series}
            model={state.model}
            width={state.width}
            onChange={updateCalcValue}
          />
        ) : (
          <CommonSelector
            label="header"
            type={state.type}
            material={state.material}
            width={state.width}
            depth={state.depth}
            thickness={state.thickness}
            onChange={setCalcValue}
            optionsData={optionsData}
            onChangeLumberSpecies={handleChangeLumberSpecies}
          />
        )}

        {shouldDisplayNumberOfPlies(state.type, pliesOptions, state.isRough) && (
          <FormGroup>
            <FormLabel htmlFor="header.ply">{plyLabel}</FormLabel>
            <Dropdown
              name="header.ply"
              value={state.ply}
              options={pliesOptions}
              onChange={setCalcValue}
              disabled={isOneDropdownOption(pliesOptions)}
              data-ga4={`header_ply:${getGAValue(pliesOptions, state.ply)}`}
              data-testid="header.ply"
            />
          </FormGroup>
        )}

        <FormGroup>
          <FormLabel htmlFor="header.memberID">Member ID</FormLabel>
          <FormControl
            name="header.memberID"
            value={state.memberID}
            onChange={(e) => {
              validateField(e)
              setCalcValue(e)
            }}
            error={errors['header.memberID']}
            data-ga4={`header_memberID:${state.memberID}`}
            data-testid="header.memberID"
          />
        </FormGroup>

        {RenderIf(
          !isNull(state.isRough),
          <FormGroup>
            <FormLabel popperText={helpText[country].roughSawn}>Lumber Finish</FormLabel>
            <Checkbox
              id="headerRough"
              name="isRough"
              label="Rough Sawn"
              checked={state.isRough}
              onChange={(e) =>
                dispatch(
                  headerActions.modifyCalculation({ name: e.target.name, value: e.target.checked }),
                )
              }
              data-ga4={`header_isRough:${state.isRough}`}
              data-testid="header.isRough"
            />
          </FormGroup>,
        )}
      </div>
    </Accordion>
  )
}

Header.propTypes = {
  country: PropTypes.string.isRequired,
}

export default Header
